/**
 * perf-trace — lightweight in-app waterfall recorder for diagnosing
 * page-load latency without changing formulas or logic.
 *
 * Enable at runtime with any of:
 *   - localStorage.setItem('PERF_TRACE', '1')
 *   - URL query ?perf=1
 *   - window.__PERF_TRACE__ = true
 *
 * Auto-installs:
 *   - React Query cache subscription (query start / finish / error)
 *   - window.fetch monkey-patch (REST + Edge Function invocations)
 *
 * Manual instrumentation entry points:
 *   perf.mark(label, meta?)     — single timestamp event
 *   perf.start(label)           — returns a stop() thunk logging duration
 *   perf.dump()                 — prints a console.table waterfall
 *
 * Every recorded event exposes:
 *   { t: ms since navigationStart, dt: ms since previous event, label, meta }
 *
 * A waterfall is auto-printed 2s after the last event so no manual call
 * is needed during a page load.
 */
import type { QueryClient, QueryCacheNotifyEvent } from "@tanstack/react-query";

type PerfEvent = {
  t: number; // ms since navigationStart / trace init
  dt: number; // delta since previous event
  label: string;
  meta?: Record<string, unknown>;
};

const T0 =
  typeof performance !== "undefined" && performance.timeOrigin
    ? performance.timeOrigin
    : Date.now();

function now(): number {
  return (typeof performance !== "undefined" ? performance.now() : Date.now() - T0);
}

function isEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if ((window as any).__PERF_TRACE__ === true) return true;
    if (typeof localStorage !== "undefined" && localStorage.getItem("PERF_TRACE") === "1") return true;
    if (typeof location !== "undefined" && /[?&]perf=1\b/.test(location.search)) return true;
  } catch {
    /* noop */
  }
  return false;
}

const enabled = isEnabled();
const events: PerfEvent[] = [];
let lastT = 0;
let dumpTimer: any = null;

function push(label: string, meta?: Record<string, unknown>) {
  if (!enabled) return;
  const t = now();
  const dt = events.length === 0 ? 0 : t - lastT;
  lastT = t;
  events.push({ t: Math.round(t), dt: Math.round(dt), label, meta });
  // eslint-disable-next-line no-console
  console.log(`[perf] +${dt.toFixed(0)}ms @${t.toFixed(0)}ms  ${label}`, meta ?? "");
  scheduleDump();
}

function scheduleDump() {
  if (dumpTimer) clearTimeout(dumpTimer);
  dumpTimer = setTimeout(() => dump("auto"), 2000);
}

function dump(reason: string = "manual") {
  if (!enabled) return;
  // eslint-disable-next-line no-console
  console.log(`[perf] ── waterfall (${reason}) — ${events.length} events, total ${Math.round(lastT)}ms ──`);
  // eslint-disable-next-line no-console
  console.table(
    events.map((e) => ({
      "t (ms)": e.t,
      "Δ (ms)": e.dt,
      label: e.label,
      meta: e.meta ? JSON.stringify(e.meta).slice(0, 160) : "",
    })),
  );
}

export const perf = {
  enabled,
  mark(label: string, meta?: Record<string, unknown>) {
    push(label, meta);
  },
  start(label: string, meta?: Record<string, unknown>) {
    if (!enabled) return () => {};
    const startedAt = now();
    push(`▶ ${label}`, meta);
    return (extra?: Record<string, unknown>) => {
      const dur = now() - startedAt;
      push(`◼ ${label} (${dur.toFixed(0)}ms)`, { ...(meta ?? {}), ...(extra ?? {}), duration_ms: Math.round(dur) });
    };
  },
  dump,
  events,
};

// ── React Query cache subscription ────────────────────────────────────
const rqStart = new Map<string, number>();
function keyStr(k: unknown): string {
  try {
    return typeof k === "string" ? k : JSON.stringify(k);
  } catch {
    return String(k);
  }
}

export function installQueryTrace(qc: QueryClient) {
  if (!enabled) return;
  const cache = qc.getQueryCache();
  cache.subscribe((event: QueryCacheNotifyEvent) => {
    const q: any = (event as any).query;
    if (!q) return;
    const key = keyStr(q.queryKey);
    const state = q.state;
    if (event.type === "observerAdded" && state?.fetchStatus === "fetching") {
      if (!rqStart.has(q.queryHash)) {
        rqStart.set(q.queryHash, now());
        push(`RQ start`, { key });
      }
    }
    if (event.type === "updated") {
      const action = (event as any).action;
      if (action?.type === "fetch") {
        if (!rqStart.has(q.queryHash)) {
          rqStart.set(q.queryHash, now());
          push(`RQ start`, { key });
        }
      } else if (action?.type === "success") {
        const started = rqStart.get(q.queryHash);
        rqStart.delete(q.queryHash);
        push(`RQ ok`, {
          key,
          duration_ms: started != null ? Math.round(now() - started) : null,
          rows: Array.isArray(action.data) ? action.data.length : undefined,
        });
      } else if (action?.type === "error") {
        const started = rqStart.get(q.queryHash);
        rqStart.delete(q.queryHash);
        push(`RQ err`, {
          key,
          duration_ms: started != null ? Math.round(now() - started) : null,
          error: String(action.error?.message ?? action.error ?? "unknown"),
        });
      }
    }
  });
  push("perf-trace: RQ subscribed");
}

// ── fetch patch — REST + Edge Functions ───────────────────────────────
export function installFetchTrace() {
  if (!enabled) return;
  if (typeof window === "undefined" || (window as any).__PERF_FETCH_PATCHED__) return;
  (window as any).__PERF_FETCH_PATCHED__ = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : (input as Request).url;
    let kind = "fetch";
    let name = url;
    try {
      const u = new URL(url, location.origin);
      if (u.pathname.includes("/functions/v1/")) {
        kind = "edge-fn";
        name = u.pathname.split("/functions/v1/")[1] ?? u.pathname;
      } else if (u.pathname.includes("/rest/v1/")) {
        kind = "rest";
        name = u.pathname.split("/rest/v1/")[1]?.split("?")[0] ?? u.pathname;
      } else if (u.pathname.includes("/auth/v1/")) {
        kind = "auth";
        name = u.pathname.split("/auth/v1/")[1] ?? u.pathname;
      } else {
        name = u.pathname;
      }
    } catch {
      /* noop */
    }
    const method = (init?.method ?? "GET").toUpperCase();
    const startedAt = now();
    push(`${kind} → ${method} ${name}`);
    try {
      const res = await orig(input, init);
      const dur = now() - startedAt;
      push(`${kind} ← ${res.status} ${name}`, { duration_ms: Math.round(dur) });
      return res;
    } catch (err) {
      const dur = now() - startedAt;
      push(`${kind} ✗ ${name}`, { duration_ms: Math.round(dur), error: String((err as any)?.message ?? err) });
      throw err;
    }
  };
  push("perf-trace: fetch patched");
}

// Expose to window for manual inspection
if (enabled && typeof window !== "undefined") {
  (window as any).__perf = perf;
}
